import { AxiosError } from 'axios';
import { AppError } from './AppError';

abstract class HttpError extends AppError {
  public readonly statusCode: number;
  public readonly payload?: unknown;
  public readonly messages: string[];

  protected constructor(
    slug: string,
    message: string,
    stackTrace: string,
    statusCode: number = -1,
    payload?: unknown,
    messages: string[] = []
  ) {
    super(slug, message, stackTrace);
    this.statusCode = statusCode;
    this.payload = payload;
    this.messages = messages;
  }

  toString = (): string =>
    `[${this.constructor.name}]: \nslug: ${this.slug}, \nmessage: ${this.message}, \nstackTrace: ${this.stackTrace}, \nstatusCode: ${this.statusCode}`;

  static parseHttpError(error: AxiosError) {
    const payload = error.response?.data as any;
    const messages = HttpError.parseMessages(payload, error.message);
    const message = messages.length > 0 ? messages.join('\n') : error.message;
    const stackTrace = error.stack || '';

    switch (error.response?.status) {
      case 400:
        return new HttpBadRequestError(
          'bad_request',
          message,
          stackTrace,
          payload,
          messages
        );
      case 401:
        return new HttpUnauthorizedError(
          'unauthorized',
          message,
          stackTrace,
          payload,
          messages
        );
      case 403:
        return new HttpForbiddenError(
          'forbidden',
          message,
          stackTrace,
          payload,
          messages
        );
      case 404:
        return new HttpNotFoundError(
          'not_found',
          message,
          stackTrace,
          payload,
          messages
        );
      case 410:
        return new HttpGoneError(
          'gone',
          message,
          stackTrace,
          payload,
          messages
        );
      case 500:
        return new HttpInternalServerError(
          'internal_server_error',
          message,
          stackTrace,
          payload,
          messages
        );
      default:
        return new HttpUnknownError(
          'unknown',
          message,
          stackTrace,
          payload,
          messages
        );
    }
  }

  private static parseMessages(payload: any, fallbackMessage: string) {
    if (Array.isArray(payload?.message)) {
      return payload.message.map((item: any) => String(item)).filter(Boolean);
    }

    if (typeof payload?.message === 'string' && payload.message.trim()) {
      return [payload.message.trim()];
    }

    if (typeof payload?.error === 'string' && payload.error.trim()) {
      return [payload.error.trim()];
    }

    if (typeof fallbackMessage === 'string' && fallbackMessage.trim()) {
      return [fallbackMessage.trim()];
    }

    return [];
  }
}

class HttpUnknownError extends HttpError {
  constructor(
    slug: string = 'unknown',
    message: string = 'Unknown error',
    stackTrace: string = '',
    payload?: unknown,
    messages: string[] = []
  ) {
    super(slug, message, stackTrace, -1, payload, messages);
  }
}

class HttpBadRequestError extends HttpError {
  constructor(
    slug: string = 'bad_request',
    message: string = 'Bad request',
    stackTrace: string = '',
    payload?: unknown,
    messages: string[] = []
  ) {
    super(slug, message, stackTrace, 400, payload, messages);
  }
}

class HttpUnauthorizedError extends HttpError {
  constructor(
    slug: string = 'unauthorized',
    message: string = 'Unauthorized',
    stackTrace: string = '',
    payload?: unknown,
    messages: string[] = []
  ) {
    super(slug, message, stackTrace, 401, payload, messages);
  }
}

class HttpForbiddenError extends HttpError {
  constructor(
    slug: string = 'forbidden',
    message: string = 'Forbidden',
    stackTrace: string = '',
    payload?: unknown,
    messages: string[] = []
  ) {
    super(slug, message, stackTrace, 403, payload, messages);
  }
}

class HttpNotFoundError extends HttpError {
  constructor(
    slug: string = 'not_found',
    message: string = 'Not found',
    stackTrace: string = '',
    payload?: unknown,
    messages: string[] = []
  ) {
    super(slug, message, stackTrace, 404, payload, messages);
  }
}

class HttpGoneError extends HttpError {
  constructor(
    slug: string = 'gone',
    message: string = 'Gone',
    stackTrace: string = '',
    payload?: unknown,
    messages: string[] = []
  ) {
    super(slug, message, stackTrace, 410, payload, messages);
  }
}

class HttpInternalServerError extends HttpError {
  constructor(
    slug: string = 'internal_server_error',
    message: string = 'Internal server error',
    stackTrace: string = '',
    payload?: unknown,
    messages: string[] = []
  ) {
    super(slug, message, stackTrace, 500, payload, messages);
  }
}

export {
  HttpError,
  HttpUnknownError,
  HttpBadRequestError,
  HttpUnauthorizedError,
  HttpForbiddenError,
  HttpNotFoundError,
  HttpGoneError,
  HttpInternalServerError
};
